import { Box, Grid, IconButton, Typography } from '@mui/material'
import Analytics, { Events } from 'analytics/Analytics'
import { ArrowRight, logo } from 'assets/icons'
import Calendar from 'components/calendar/Calendar'
import { useEffect, useState } from 'react'

import { useNavigate } from 'react-router-dom'

import Database from 'services/db/Database'

import './styles.scss'
import {
  CalendarRange,
  getCalendarRangeByString
} from 'types/components/calendar'
import { useCalendar } from 'components/providers/CalendarProvider'
import { t } from 'i18next'
import { APP_LINK, isMobileDevice } from 'utils/constants'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import { getEventsForGroup } from 'services/api/event'
import dayjs, { Dayjs } from 'dayjs'
import { GroupDoc } from 'types/api/changes'
import useFitText from 'use-fit-text'
import { AxiosError } from 'axios'
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog'
import EmailHighlight from 'components/dialogs/embed/EmailHighlight'

export interface GuestModeProps {
  showHeader?: string
  defaultView?: CalendarRange
  groupId: string
  width?: string
  height?: string
  preview?: boolean
  groupsIds?: string[]
  calendarTitle?: string
}

export default function GuestMode(props: GuestModeProps) {
  const navigate = useNavigate()
  const calendar = useCalendar()

  const [group, onGroup] = useState<GroupDoc | undefined>(undefined)
  const [showNotValidCalendar, setShowNotValidCalendar] = useState(false)

  const goToLogin = () => {
    Analytics.getInstance().sendEvent(Events.GEUST_MODE_OPEN_FULL_VERSION)

    Database.clear().then(() => {
      if (isMobileDevice()) {
        // Open new page in the same tab with mobile link
        window.location.href = `${APP_LINK}/${LocalStorage.get(
          LocalStorageKey.MOBILE_LINK
        )}`
      } else {
        navigate('/')
      }
    })
  }

  const isEmbed = window.self !== window.top

  useEffect(() => {
    Analytics.getInstance().sendEvent(Events.GEUST_MODE_OPEN)
  }, [])

  const openMainPage = () => {
    window.open('https://app.groupcal.app/')
  }

  const queryParams = new URLSearchParams(location.search)
  const showHeader = queryParams.get('showHeader')

  useEffect(() => {
    calendar.onView(
      getCalendarRangeByString(
        LocalStorage.get(LocalStorageKey.CALENDAR_VIEW) ?? ''
      ) ?? CalendarRange.DAY
    )
  }, [])

  useEffect(() => {
    if (!props.preview)
      getEventsForGroup(
        LocalStorage.get(LocalStorageKey.CACHED_GROUP_ID_JOIN) ?? '',
        dayjs().unix(),
        dayjs().unix(),
        props.groupsIds
      )
        .then((eventsAndGroupData) => {
          onGroup(eventsAndGroupData.groups[0])
        })
        .catch((error) => {
          if (error instanceof AxiosError) {
            const httpCode = error.response?.status

            if (httpCode === 403) {
              const code = (error.response?.data as unknown as any)['code']
              /**
               * we have specific cases when server return 403, but we should not retry the request, such as:
               * out of tier, user blocked from adding/editing events etc
               *
               */

              if (code === -113) {
                setShowNotValidCalendar(true)
              }
            }
          }
        })
  }, [])

  const { fontSize, ref } = useFitText({
    minFontSize: 50,
    maxFontSize: 100
  })

  return (
    <Box
      bgcolor="green"
      display="flex"
      className="guest-root"
      height={props.height ? props.height : '100dvh'}
      width={props.width ? props.width : '100dvw'}
    >
      <Box
        bgcolor={isEmbed || props.preview ? '#ffffff' : '#e5E5E5'}
        display="flex"
        flex={1}
        minWidth={'100px'}
        minHeight={'100px'}
        flexDirection={{ xs: 'column', sm: 'column' }}
      >
        {showHeader ?? props.showHeader === '0' ? (
          <></>
        ) : (
          <Box paddingLeft={4} paddingRight={4} paddingTop={4}>
            <Box>
              <Grid
                direction={'row'}
                container
                gap={'64px'}
                wrap={'nowrap'}
                display={'flex'}
                alignItems={'center'}
                justifyContent={isMobileDevice() ? 'center' : 'space-between'}
              >
                {isMobileDevice() ? (
                  <></>
                ) : (
                  <Grid
                    item
                    xs={2}
                    container
                    wrap={'nowrap'}
                    direction={'row'}
                    gap={'11px'}
                    display={'flex'}
                    alignItems={'center'}
                  >
                    <IconButton onClick={openMainPage}>
                      <img
                        src={logo}
                        style={{ width: 36, height: 36, cursor: 'pointer' }}
                      />
                    </IconButton>
                    <span onClick={openMainPage} style={{ cursor: 'pointer' }}>
                      <Typography fontFamily={'Montserrat'} fontSize={'24px'}>
                        GroupCal
                      </Typography>
                    </span>
                  </Grid>
                )}
                <Box display={'flex'} flexDirection={'column'}>
                  {isMobileDevice() && group ? (
                    <Typography
                      fontWeight={600}
                      fontSize={'1.5rem'}
                      width={'100%'}
                      textAlign={'center'}
                    >
                      {group?.Name}
                    </Typography>
                  ) : (
                    <></>
                  )}
                  <div
                    ref={ref}
                    style={{
                      height: isMobileDevice() ? '30px' : '50px',
                      fontSize: fontSize
                    }}
                    className="goToSignUp"
                    onClick={goToLogin}
                  >
                    {t('joinCalnedar')}
                    <ArrowRight className="arrow" style={{ marginLeft: 8 }} />
                  </div>
                </Box>
              </Grid>
            </Box>
          </Box>
        )}
        <Box height={'100%'} padding={4}>
          <Calendar
            calendarTitle={props.calendarTitle}
            showOnlySelectedGroup
            groupId={props.groupId}
            groupIds={props.groupsIds}
            readOnly
          />
        </Box>
      </Box>
      {showNotValidCalendar && (
        <ConfirmationDialog
          leftButtonHidden
          handleRightButton={() => {
            setShowNotValidCalendar(false)
          }}
          title={t('notValidCalendar')}
          content={<EmailHighlight text={t('notValidCalendarDesc')} />}
          open
        />
      )}
    </Box>
  )
}
